// src/services/syncService.js
// VERSION EXACTE : Colonnes correspondant à votre schéma Supabase

import { supabase } from '../supabaseClient';
import { extractPointsFromMatch, syncPointsToSupabase, calculateAndSaveMatchStatistics } from './pointsService';

/**
 * Initialiser les métadonnées de synchro pour un match
 */
const initSyncMetadata = (match) => {
  if (!match.syncMetadata) {
    match.syncMetadata = {
      matchLastSyncedAt: null,
      pointsLastSyncedAt: null,
      pointsCount: 0
    };
  }
  return match;
};

/**
 * Synchronisation intelligente d'un seul match
 * - Matches : synchro toutes les 2 min (avec engineState complet)
 * - Points : synchro SEULEMENT si status = 'FINISHED'
 */
export const syncMatch = async (match) => {
  console.log(`\n🔄 Synchronisation du match ${match.id}`);
  
  initSyncMetadata(match);
  
  const needsMatchSync = !match.syncMetadata.matchLastSyncedAt || 
                         (match.lastModified > match.syncMetadata.matchLastSyncedAt);
  
  const localPointsCount = match.engineState?.points?.length || 0;
  const lastSyncedCount = match.syncMetadata.pointsCount || 0;
  const needsPointsSync = localPointsCount !== lastSyncedCount;
  const isFinished = match.status === 'FINISHED';
  
  console.log(`  Match modifié: ${needsMatchSync ? 'OUI' : 'NON'}`);
  console.log(`  Points: local=${localPointsCount}, synced=${lastSyncedCount}, besoin sync: ${needsPointsSync ? 'OUI' : 'NON'}`);
  console.log(`  Statut: ${match.status || 'EN_COURS'} ${isFinished ? '✅' : '🎾'}`);
  
  let updated = false;
  
  // ========================================
  // 1. ✅ TOUJOURS synchroniser le match lui-même
  // ========================================
  if (needsMatchSync) {
    console.log(`  📝 Synchronisation des données du match...`);
    
    // 🆕 Mapper les noms de colonnes correctement
    const updateData = {
      // Joueurs
      joueur1Nom: match.joueur1Nom || null,
      joueur2Nom: match.joueur2Nom || null,
      prenomJoueur1: match.prenomJoueur1 || null,
      prenomJoueur2: match.prenomJoueur2 || null,
      joueur1Club: match.joueur1Club || null,
      joueur2Club: match.joueur2Club || null,
      joueur1ClassementCode: match.joueur1ClassementCode || null,
      joueur2ClassementCode: match.joueur2ClassementCode || null,
      joueur1PaysCode: match.joueur1PaysCode || null,
      joueur2PaysCode: match.joueur2PaysCode || null,
      joueur1Droitier: match.joueur1Droitier ?? null,
      joueur2Droitier: match.joueur2Droitier ?? null,
      
      // Date et tournoi
      dateMatch: match.dateMatch,
      tournoiNom: match.tournoiNom || null,
      
      // Surface et catégorie
      surfaceCode: match.tournoiSurface || match.surfaceCode || null,
      categorieCode: match.tournoiCategorie || match.categorieCode || null,
      tourCode: match.tourCode || null,
      categorieageCode: match.categorieageCode || null,
      epreuveCode: match.epreuveCode || null,
      toss: match.toss || null,
      
      // Formats (sans "Code" à la fin)
      formatMatch: match.formatMatch || 'BEST_OF_3',
      formatSet1: match.formatSet1Code || match.formatSet1 || 'STD',
      formatSet2: match.formatSet2Code || match.formatSet2 || 'STD',
      formatSet3: match.formatSet3Code || match.formatSet3 || 'STD',
      formatSet4: match.formatSet4Code || match.formatSet4 || null,
      formatSet5: match.formatSet5Code || match.formatSet5 || null,
      
      // Premier serveur
      premierServeur: match.premierServeur || 'J1',
      
      // Score et état (JSONB)
      currentScore: match.currentScore,
      engineState: match.engineState,  // ✅ Contient TOUS les points !
           
      // Commentaires
      matchComments: match.matchComments || null,
      
      // Statut
      status: match.status || 'DRAFT',
      
      // Timestamps
      updatedAt: new Date().toISOString()
    };
          // 🔍 AJOUTE CE LOG JUSTE APRÈS
      console.log('🔍 engineState envoyé à Supabase:', JSON.stringify(match.engineState));
    
    // 🆕 VÉRIFIER SI LE MATCH EXISTE DANS SUPABASE
    const { data: existing, error: checkError } = await supabase
      .from('matches')
      .select('id, "updatedAt"')
      .eq('id', match.id)
      .maybeSingle();
    
    if (checkError) {
      console.error(`  ❌ Erreur vérification existence:`, checkError.message);
      return { success: false, error: checkError.message };
    }
    
    // 🛡️ PROTECTION : si la version du cloud est plus récente que notre copie locale
    // (ex. modification faite ailleurs, fusion de joueurs par un admin...),
    // on n'écrase PAS le cloud avec une copie locale périmée.
    // Le cloud sera récupéré au prochain chargement de la liste (getAllMatches).
    if (existing) {
      const localStamp = new Date(match.lastModified || match.updatedAt || 0).getTime();
      const cloudStamp = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
      if (cloudStamp > localStamp) {
        console.log(`  🛡️ Cloud plus récent que la copie locale → pas d'écrasement`);
        return { success: true, updated: false, needsMatchSync: false, needsPointsSync, isFinished, skippedCloudNewer: true };
      }
    }

    let error;
    
    if (existing) {
      // Match existe → UPDATE
      console.log(`  🔄 Mise à jour du match existant...`);
      const result = await supabase
        .from('matches')
        .update(updateData)
        .eq('id', match.id);
      error = result.error;
    } else {
      // Match n'existe pas → INSERT
      console.log(`  ✨ Création du match dans Supabase...`);
      const result = await supabase
        .from('matches')
        .insert({
          id: match.id,  // ✅ Inclure l'ID pour INSERT
          // Liaison avec les fiches joueurs (uniquement à la création du match dans le cloud)
          ...(match.player1Id && { player1Id: match.player1Id }),
          ...(match.player2Id && { player2Id: match.player2Id }),
          ...updateData
        });
      error = result.error;
    }
    
    if (error) {
      console.error(`  ❌ Erreur sync match:`, error.message);
      return { success: false, error: error.message };
    } else {
      match.syncMetadata.matchLastSyncedAt = new Date().toISOString();
      updated = true;
      console.log(`  ✅ Match synchronisé (avec ${localPointsCount} points dans engineState)`);
    }
  }
  
  // ========================================
  // 2. 🆕 Synchroniser les points SEULEMENT si FINISHED
  // ========================================
  if (isFinished && needsPointsSync) {
    console.log(`  🎾 Match terminé → Synchronisation de ${localPointsCount} points dans la table points...`);
    
    const points = extractPointsFromMatch(match);
    
    if (points.length > 0) {
      const result = await syncPointsToSupabase(match.id, points);
      
      if (result.success) {
        match.syncMetadata.pointsCount = localPointsCount;
        match.syncMetadata.pointsLastSyncedAt = new Date().toISOString();
        updated = true;
        
        console.log(`  📊 Calcul des statistiques...`);
        await calculateAndSaveMatchStatistics(match.id);
        console.log(`  ✅ ${result.synced} points synchronisés + stats sauvegardées`);
      } else {
        console.error(`  ❌ Erreur sync points:`, result.error);
      }
    }
  } else if (!isFinished && needsPointsSync) {
    console.log(`  ⏭️  Match en cours → Points dans engineState, pas encore dans table points`);
  } else if (isFinished && !needsPointsSync) {
    console.log(`  ✅ Points déjà synchronisés (${lastSyncedCount} points)`);
  }
  
  if (!needsMatchSync && !needsPointsSync) {
    console.log(`  ⏭️  Aucune modification`);
  }
  
  return {
    success: true,
    updated,
    needsMatchSync,
    needsPointsSync,
    isFinished
  };
};

/**
 * Synchronisation intelligente de TOUS les matchs
 */
export const syncAllMatches = async () => {
  console.log('🚀 Synchronisation intelligente de tous les matchs...\n');
  
  const matchesList = JSON.parse(localStorage.getItem('matches') || '[]');
  
  if (matchesList.length === 0) {
    console.log('⚠️ Aucun match à synchroniser');
    return { success: true, total: 0, synced: 0, skipped: 0 };
  }
  
  let syncedCount = 0;
  let skippedCount = 0;
  const results = [];
  
  for (const matchFromList of matchesList) {
    const matchId = matchFromList.id || matchFromList.matchId;
    
    const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(matchId);
    if (!isValidUUID) {
      console.log(`⏭️ Skip ${matchId} (pas un UUID)`);
      skippedCount++;
      continue;
    }
    
    const storageKey = `match_${matchId}`;
    const savedData = localStorage.getItem(storageKey);
    
    if (!savedData) {
      console.log(`⏭️ Skip ${matchId} (fichier introuvable)`);
      skippedCount++;
      continue;
    }
    
    const match = JSON.parse(savedData);
    
    try {
      const result = await syncMatch(match);
      
      if (result.updated) {
        syncedCount++;
        
        localStorage.setItem(storageKey, JSON.stringify(match));
        
        const matches = JSON.parse(localStorage.getItem('matches') || '[]');
        const index = matches.findIndex(m => m.id === matchId);
        if (index !== -1) {
          matches[index] = match;
          localStorage.setItem('matches', JSON.stringify(matches));
        }
      } else {
        skippedCount++;
      }
      
      results.push({ matchId, ...result });
      
    } catch (error) {
      console.error(`❌ Erreur match ${matchId}:`, error.message);
      results.push({ matchId, error: error.message });
    }
  }
  
  console.log(`\n✅ Synchronisation terminée:`);
  console.log(`   Total: ${matchesList.length} matchs`);
  console.log(`   Synchronisés: ${syncedCount}`);
  console.log(`   Skippés: ${skippedCount}`);
  
  return {
    success: true,
    total: matchesList.length,
    synced: syncedCount,
    skipped: skippedCount,
    results
  };
};

/**
 * Marquer un match comme ayant besoin d'une synchronisation
 */
export const markMatchAsModified = (matchId) => {
  const storageKey = `match_${matchId}`;
  const savedData = localStorage.getItem(storageKey);
  
  if (savedData) {
    const match = JSON.parse(savedData);
    const now = new Date().toISOString();
    match.lastModified = now;
    match.updatedAt = now;  // 🆕 Aussi mettre à jour updatedAt pour le merge local/cloud
    localStorage.setItem(storageKey, JSON.stringify(match));
    
    const matches = JSON.parse(localStorage.getItem('matches') || '[]');
    const index = matches.findIndex(m => m.id === matchId);
    if (index !== -1) {
      matches[index].lastModified = now;
      matches[index].updatedAt = now;  // 🆕
      localStorage.setItem('matches', JSON.stringify(matches));
    }
    
    console.log(`🔔 Match ${matchId} marqué comme modifié`);
  }
};

/**
 * Marquer un match comme terminé et forcer la synchronisation des points
 */
export const finishMatch = async (matchId) => {
  console.log(`🏁 Finalisation du match ${matchId}...`);
  
  const storageKey = `match_${matchId}`;
  const savedData = localStorage.getItem(storageKey);
  
  if (!savedData) {
    console.error(`❌ Match ${matchId} introuvable`);
    return { success: false, error: 'Match introuvable' };
  }
  
  const match = JSON.parse(savedData);
  
  match.status = 'FINISHED';
  match.lastModified = new Date().toISOString();
  
  localStorage.setItem(storageKey, JSON.stringify(match));
  
  const matches = JSON.parse(localStorage.getItem('matches') || '[]');
  const index = matches.findIndex(m => m.id === matchId);
  if (index !== -1) {
    matches[index].status = 'FINISHED';
    matches[index].lastModified = match.lastModified;
    localStorage.setItem('matches', JSON.stringify(matches));
  }
  
  console.log(`✅ Match marqué comme FINISHED`);
  
  const result = await syncMatch(match);
  
  if (result.success) {
    localStorage.setItem(storageKey, JSON.stringify(match));
    
    console.log(`✅ Match finalisé et synchronisé`);
    return { success: true, result };
  } else {
    console.error(`❌ Erreur lors de la synchronisation`);
    return { success: false, error: result.error };
  }
};